import React, { useEffect, useState, useCallback } from 'react';
import PageHero from '../components/site/PageHero';
import { CtaBanner } from '../components/site/BottomSections';
import { X, ChevronLeft, ChevronRight, ZoomIn, AlertTriangle, ImageOff } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function Media() {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [active, setActive] = useState(null); // index

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/gallery`);
        if (!alive) return;
        // Prefix backend origin to relative /api/gallery-files/... URLs
        const urls = (data.images || []).map((f) => ({
          ...f,
          src: f.url.startsWith('http') ? f.url : `${BACKEND}${f.url}`,
          caption: f.name.replace(/^\d+[-_ ]?/, '').replace(/\.[^.]+$/, '').replace(/[-_]/g, ' '),
        }));
        setImages(urls);
      } catch (err) {
        setError('Could not load the gallery just now. Please try again.');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const close = useCallback(() => setActive(null), []);
  const prev = useCallback(() => setActive((i) => (i === null ? null : (i - 1 + images.length) % images.length)), [images.length]);
  const next = useCallback(() => setActive((i) => (i === null ? null : (i + 1) % images.length)), [images.length]);

  useEffect(() => {
    if (active === null) return;
    const onKey = (e) => {
      if (e.key === 'Escape') close();
      else if (e.key === 'ArrowLeft') prev();
      else if (e.key === 'ArrowRight') next();
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [active, close, prev, next]);

  return (
    <div>
      <PageHero
        eyebrow="Gallery & Media"
        title="Views worth waking up to."
        subtitle="A visual walk through the townships, the towns and the trails."
        image="/gallery_2025/Firefly 20250419114040.png"
      />

      <section className="bg-[#f6f1e7] py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div className="h-px w-12 bg-[#8a6b3a]" />
              <span className="text-[#8a6b3a] text-xs uppercase tracking-[0.3em]">
                {loading ? 'Loading gallery…' : `${images.length} Photos`}
              </span>
            </div>
            {/* <div className="text-[#0f1a14]/50 text-xs">Drop new photos into <code className="bg-white/60 px-2 py-1">/backend/gallery_2026/</code> to add them here.</div> */}
          </div>

          {error && (
            <div className="flex items-center gap-2 text-red-700 border border-red-200 bg-red-50 p-4 mb-8">
              <AlertTriangle className="h-4 w-4" /> {error}
            </div>
          )}

          {!loading && !error && images.length === 0 && (
            <div className="text-center py-24 border border-dashed border-[#0f1a14]/20 text-[#0f1a14]/50">
              <ImageOff className="h-8 w-8 mx-auto mb-4 text-[#8a6b3a]" />
              No photos yet. Add images to <code>/backend/gallery_2026/</code> and refresh.
            </div>
          )}

          <div className="columns-1 sm:columns-2 lg:columns-3 gap-4 [column-fill:_balance]">
            {loading &&
              Array.from({ length: 9 }).map((_, i) => (
                <div key={i} className="mb-4 break-inside-avoid animate-pulse bg-[#0f1a14]/10" style={{ height: 220 + (i % 3) * 80 }} />
              ))}

            {!loading &&
              images.map((img, i) => (
                <button
                  key={img.name}
                  type="button"
                  onClick={() => setActive(i)}
                  aria-label={`Open ${img.caption || 'image'} in fullscreen`}
                  className="mb-4 break-inside-avoid overflow-hidden group relative block w-full cursor-zoom-in focus:outline-none focus:ring-2 focus:ring-[#8a6b3a] focus:ring-offset-2 focus:ring-offset-[#f6f1e7]"
                >
                  <img
                    src={img.src}
                    alt={img.caption || `Ewen Realtors gallery ${i + 1}`}
                    loading="lazy"
                    className="w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-[#0f1a14]/0 group-hover:bg-[#0f1a14]/40 transition-colors duration-500 grid place-items-end">
                    <div className="w-full p-4 flex items-center justify-between opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                      <div className="text-white font-serif text-lg capitalize truncate">{img.caption}</div>
                      <div className="h-9 w-9 grid place-items-center bg-[#c9a56a] shrink-0">
                        <ZoomIn className="h-4 w-4 text-[#0f1a14]" />
                      </div>
                    </div>
                  </div>
                </button>
              ))}
          </div>
        </div>
      </section>

      {active !== null && images[active] && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Gallery image viewer"
          className="fixed inset-0 z-[90] bg-[#0f1a14]/95 backdrop-blur-sm flex items-center justify-center"
          onClick={close}
        >
          <button
            onClick={(e) => { e.stopPropagation(); close(); }}
            aria-label="Close"
            className="absolute top-5 right-5 h-11 w-11 grid place-items-center text-white/80 hover:text-white border border-white/20 hover:border-[#c9a56a] transition-colors"
          >
            <X className="h-5 w-5" />
          </button>

          <button
            onClick={(e) => { e.stopPropagation(); prev(); }}
            aria-label="Previous image"
            className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 h-12 w-12 grid place-items-center text-white/80 hover:text-white border border-white/20 hover:border-[#c9a56a] transition-colors"
          >
            <ChevronLeft className="h-6 w-6" />
          </button>

          <div
            className="relative max-w-[92vw] max-h-[86vh] flex flex-col items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              key={active}
              src={images[active].src}
              alt={images[active].caption || ''}
              className="max-w-full max-h-[86vh] object-contain shadow-2xl animate-[fadeUp_500ms_ease-out]"
            />
            {images[active].caption && (
              <div className="mt-4 text-white/70 text-sm capitalize">{images[active].caption}</div>
            )}
          </div>

          <button
            onClick={(e) => { e.stopPropagation(); next(); }}
            aria-label="Next image"
            className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 h-12 w-12 grid place-items-center text-white/80 hover:text-white border border-white/20 hover:border-[#c9a56a] transition-colors"
          >
            <ChevronRight className="h-6 w-6" />
          </button>

          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/70 text-xs uppercase tracking-[0.3em]">
            <span className="text-[#c9a56a]">{String(active + 1).padStart(2, '0')}</span>
            <span className="text-white/40"> / {String(images.length).padStart(2, '0')}</span>
          </div>
        </div>
      )}

      <CtaBanner />
    </div>
  );
}
